import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Api from '../../Services/Api';
import * as Sentry from "@sentry/browser";
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const CompanyAutocomplete = ({ opts, company, setCompany }) => {
    const { t } = useTranslation();
    // Pre-seed options with the current company so defaultValue resolves correctly
    const [options, setOptions] = useState(() =>
        opts?.length > 0 ? opts : (company?.id && company?.name ? [company] : [])
    );
    const [after, setAfter] = useState(undefined);
    const [inputValue, setInputValue] = useState(
        opts?.length > 0
            ? opts.find((option) => option.id === company.id)
            : (company?.id && company?.name ? company : undefined)
    );
    const previousController = useRef();

    useEffect(() => {
        if (opts && opts.length > 0) {
            setOptions(opts);
        } else if (company?.id && company?.name && options.length === 0) {
            setOptions([company]);
        }
    }, [opts]);

    const onInputChangeCompany = (event, value, reason) => {
        if (value && reason === "input") {
            if (previousController.current) {
                previousController.current.abort();
            }
            const controller = new AbortController();
            const signal = controller.signal;
            previousController.current = controller;

            Api.fetchCompanies({value: value, after: after, signal: signal})
                .then((result) => {
                    if (result.options.length > 0) {
                        setOptions(result.options);
                    }
                    setAfter(result.after);
                })
                .catch((error) => {
                    setAfter(undefined);
                    Sentry.captureException(error);
                    console.log(error);
                });
        }
    };

    return (
        <Autocomplete
            disablePortal
            id="company-autocomplete"
            style={{ width: "100%" }}
            defaultValue={inputValue}
            onInputChange={onInputChangeCompany}
            onChange={(event, newValue) => {
                if (newValue && newValue.id) {
                    setCompany({ id: newValue.id, name: newValue.name, domain: newValue.domain });
                }
            }}
            options={options.sort((a, b) => {
                if (a.name && b.name) {
                    return a.name.localeCompare(b.name);
                }
                return 0;
            })}
            groupBy={(option) => option.name?.charAt(0)?.toUpperCase() || '#'}
            autoSelect={true}
            getOptionLabel={(option) => (option.name || '') + (option.domain ? ' ' + option.domain : '')}
            slotProps={{
                paper: {
                    sx: {
                        '& .MuiAutocomplete-option': {
                            minHeight: 52,
                            padding: '10px 16px',
                        }
                    }
                }
            }}
            renderInput={(params) => <TextField {...params} label={t('selectCompany')} />}
            renderOption={(props, option) => (
                <Stack component="li" direction="row" alignItems="center" spacing={1.5}
                    sx={{ minHeight: 56, py: 1, px: 2 }} {...props}>
                    <Avatar sx={{
                        width: 36, height: 36,
                        backgroundColor: 'rgba(118,169,172,0.3)',
                        color: '#76A9AC', fontSize: '0.875rem', fontWeight: 600,
                    }}>
                        {(option.name?.[0] || '').toUpperCase()}
                    </Avatar>
                    <Stack spacing={0}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.name || ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {option.domain || ''}
                        </Typography>
                    </Stack>
                </Stack>
            )}
        />
    );
}

export default CompanyAutocomplete;
